import placeholderImage from '@legit-health/svg-images/images/instructions/photo-of-dermatitis.svg';
import {FILE_MAX_SIZE, ImageWithCrop, MediaDetection} from '@legit-health/ui';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import {Alert, Box, Chip, Paper, Stack, Typography} from '@mui/material';
import {TFunction} from 'i18next';
import ImageField from '@/shared/components/diagnosticReport/DiagnosticReportForm/components/FollowUpForm/components/ImageField';
import type {SlotStatesByKey} from '../HidradenitisSuppurativaWizard';
import type {ProtocolRegion} from '../regions';
import calculateIhs4Score from '../utils/calculateIhs4Score';
import DetectionEditor from './DetectionEditor';

interface RegionStepProps {
  region: ProtocolRegion;
  isSkipped: boolean;
  slotStates: SlotStatesByKey;
  onImageChange: (slotId: string, image: ImageWithCrop | null) => void;
  onDetectionsChange: (slotId: string, detections: MediaDetection[]) => void;
  t: TFunction;
}

export default function RegionStep({
  region,
  isSkipped,
  slotStates,
  onImageChange,
  onDetectionsChange,
  t
}: RegionStepProps) {
  const sizeErrorMessage = t('ui:dropzone.error.fileSize', {maxSize: FILE_MAX_SIZE});
  const generalErrorMessage = t('ui:dropzone.error.general');
  const typeErrorMessage = t('ui:dropzone.error.type');

  const regionName = t('shared/imageProtocol:hs.regions.' + region.id + '.name');
  const instructions = t('shared/imageProtocol:hs.regions.' + region.id + '.instructions', {
    returnObjects: true
  }) as string[];
  const tips = t('shared/imageProtocol:hs.regions.' + region.id + '.tips', {
    returnObjects: true
  }) as string[];

  const slotState = slotStates[region.id];
  const isAssessed = slotState?.phase === 'assessed' && slotState.image.file;
  const showError = slotState?.phase === 'invalid' || Boolean(slotState?.errorMessage);

  return (
    <Stack spacing={3}>
      <Paper variant="outlined" sx={{p: {xs: 2, sm: 3}}}>
        <Stack spacing={1.5}>
          <Typography
            variant="overline"
            sx={{color: 'primary.main', fontWeight: 700, letterSpacing: 1}}
          >
            {t('shared/imageProtocol:instructions')}
          </Typography>
          <Stack spacing={0.75}>
            {instructions.map((instruction, i) => (
              <Box key={i} sx={{display: 'flex', alignItems: 'flex-start', gap: 1}}>
                <ChevronRightIcon fontSize="small" sx={{color: 'primary.main', mt: '2px'}} />
                <Typography variant="body2" sx={{color: 'text.primary'}}>
                  {instruction}
                </Typography>
              </Box>
            ))}
          </Stack>

          {tips.length > 0 && (
            <>
              <Typography
                variant="overline"
                sx={{color: 'warning.main', fontWeight: 700, letterSpacing: 1, pt: 1}}
              >
                {t('shared/imageProtocol:tips')}
              </Typography>
              <Stack spacing={0.75}>
                {tips.map((tip, i) => (
                  <Box key={i} sx={{display: 'flex', alignItems: 'flex-start', gap: 1}}>
                    <LightbulbOutlinedIcon
                      fontSize="small"
                      sx={{color: 'warning.main', mt: '2px'}}
                    />
                    <Typography variant="body2" sx={{color: 'text.secondary'}}>
                      {tip}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </>
          )}
        </Stack>
      </Paper>

      {isSkipped ? (
        <Alert
          icon={<SkipNextIcon fontSize="inherit" />}
          severity="info"
          sx={{justifyContent: 'center'}}
        >
          {t('shared/imageProtocol:regionSkipped')}
        </Alert>
      ) : (
        <Stack spacing={1}>
          {isAssessed && slotState.image.file ? (
            <>
              <Chip
                size="small"
                color="success"
                label={t('shared/imageProtocol:hs.severityScore', {
                  score: calculateIhs4Score(slotState.detections)
                })}
                sx={{alignSelf: 'flex-start'}}
              />
              <DetectionEditor
                file={slotState.image.file}
                detections={slotState.detections}
                onChange={(detections) => onDetectionsChange(region.id, detections)}
                t={t}
              />
            </>
          ) : (
            <>
              {showError && slotState?.errorMessage && (
                <Alert icon={<ErrorOutlineIcon />} severity="warning">
                  {slotState.errorMessage}
                </Alert>
              )}
              <ImageField
                value={
                  slotState?.image ?? {
                    crop: null,
                    file: null,
                    id: region.id
                  }
                }
                placeholderImage={placeholderImage}
                label={regionName}
                name={region.id}
                sizeErrorMessage={sizeErrorMessage}
                generalErrorMessage={generalErrorMessage}
                typeErrorMessage={typeErrorMessage}
                onChange={(image) => onImageChange(region.id, image)}
                dataTestId={`imageProtocolCapture-${region.id}`}
              />
            </>
          )}
        </Stack>
      )}
    </Stack>
  );
}
